import React, { useState, useEffect, useCallback } from 'react';

const API = (path, opts = {}) => {
  const token = localStorage.getItem('token');
  return fetch(path, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers } });
};

// ── Data ──
const ALL_PLANS = [
  { id: 'monthly',   label: 'Monthly',   months: 1,  discount: 0,  days: 30,  interval: 'month',    badge: null },
  { id: 'quarterly', label: 'Quarterly', months: 3,  discount: 10, days: 90,  interval: '3 months', badge: 'Save 10%' },
  { id: 'annual',    label: 'Annual',    months: 12, discount: 25, days: 365, interval: 'year',     badge: 'Best Value' },
];

const BILLING_MODES = [
  { id: 'upfront',   label: 'Pay Upfront',   icon: '💎', desc: 'Pay the full period now and get a discount.' },
  { id: 'recurring', label: 'Pay-as-you-go', icon: '🔄', desc: 'Monthly charge from your balance. No discount, but lower entry cost.' },
];

const BOOST_PRESETS = [2, 8, 14, 20, 30];

// ── Helpers ──
const calcPrice = (boosts, ppb, plan, billingMode, tokenType) => {
  const monthly = boosts * ppb;
  if (billingMode === 'recurring') {
    // 3m tokens: charge per plan cycle (e.g. quarterly = 3mo, annual = 12mo)
    // 1m tokens: charge monthly
    if (tokenType === '3m') {
      const cycleTotal = monthly * plan.months;
      return { monthly, fullPrice: cycleTotal, discount: 0, total: cycleTotal, perMonth: monthly, months: plan.months, cycleDays: plan.days };
    }
    return { monthly, fullPrice: monthly, discount: 0, total: monthly, perMonth: monthly, months: 1, cycleDays: 30 };
  }
  const full = monthly * plan.months;
  const disc = full * (plan.discount / 100);
  const tot = full - disc;
  return { monthly, fullPrice: full, discount: disc, total: tot, perMonth: tot / plan.months, months: plan.months, cycleDays: plan.days };
};

const Countdown = ({ date }) => {
  const [t, setT] = useState('');
  useEffect(() => {
    const calc = () => {
      const d = new Date(date) - new Date();
      if (d <= 0) return setT('Now');
      const days = Math.floor(d / 864e5), hrs = Math.floor((d % 864e5) / 36e5);
      setT(days > 30 ? `${days}d` : days > 0 ? `${days}d ${hrs}h` : `${hrs}h ${Math.floor((d % 36e5) / 6e4)}m`);
    };
    calc();
    const i = setInterval(calc, 60000);
    return () => clearInterval(i);
  }, [date]);
  return <span>{t}</span>;
};

// ── Main Component ──
const AutoReboost = ({ balance, onBalanceChange }) => {
  const [view, setView] = useState('list');      // list | create | detail
  const [subs, setSubs] = useState([]);
  const [sel, setSel] = useState(null);
  const [modal, setModal] = useState(null);       // cancel | retry-fail
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  // Stock-aware config
  const [reboostConfig, setReboostConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);

  // Create form
  const [plan, setPlan] = useState('monthly');
  const [billing, setBilling] = useState('upfront');
  const [qty, setQty] = useState(14);
  const [invite, setInvite] = useState('');
  const [inviteOk, setInviteOk] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Fetch reboost config (stock-aware pricing)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch('/api/reboost-config');
        if (res.ok) {
          const data = await res.json();
          setReboostConfig(data);
          // Auto-select first available plan
          if (data.availablePlans && data.availablePlans.length > 0 && !data.availablePlans.includes(plan)) {
            setPlan(data.availablePlans[0]);
          }
        }
      } catch (e) { console.error('Failed to load reboost config:', e); }
      finally { setConfigLoading(false); }
    };
    fetchConfig();
    const i = setInterval(fetchConfig, 15000);
    return () => clearInterval(i);
  }, [plan]);

  // Derive pricing from config
  const ppb = reboostConfig?.pricePerBoost || 0.20;
  const availablePlans = reboostConfig?.availablePlans || ['monthly', 'quarterly', 'annual'];
  const PLANS = ALL_PLANS.filter(p => availablePlans.includes(p.id));
  const isInStock = reboostConfig?.inStock !== false;
  const tokenType = reboostConfig?.tokenType || '1m';

  const planObj = PLANS.find(p => p.id === plan) || PLANS[0];
  const price = calcPrice(qty, ppb, planObj, billing, tokenType);

  const activeCount = subs.filter(s => s.status === 'active').length;
  const failedCount = subs.filter(s => s.status === 'failed').length;
  const totalBoosts = subs.filter(s => s.status === 'active').reduce((a, s) => a + s.boosts, 0);

  // Load subscriptions
  const loadSubs = useCallback(async () => {
    try {
      const res = await API('/api/user/reboost-subs');
      if (res.ok) {
        const data = await res.json();
        setSubs(data.subscriptions || []);
      }
    } catch (e) { console.error('Failed to load reboost subs:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSubs(); }, [loadSubs]);

  // Resolve invite
  useEffect(() => {
    if (!invite || invite.length < 3) { setInviteOk(null); return; }
    setInviteLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await API('/api/user/resolve-invite', {
          method: 'POST',
          body: JSON.stringify({ invite }),
        });
        if (res.ok) {
          const data = await res.json();
          setInviteOk(data);
        } else {
          setInviteOk(null);
        }
      } catch { setInviteOk(null); }
      finally { setInviteLoading(false); }
    }, 600);
    return () => clearTimeout(t);
  }, [invite]);

  // Create subscription
  const handleCreate = async () => {
    if (balance < price.total) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await API('/api/user/reboost-subs', {
        method: 'POST',
        body: JSON.stringify({ serverInvite: invite, plan, billingMode: billing, boosts: qty }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setDone(true);
      if (onBalanceChange) onBalanceChange();
      setTimeout(() => { setDone(false); setView('list'); setInvite(''); setInviteOk(null); loadSubs(); }, 2000);
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally { setBusy(false); }
  };

  // Retry failed
  const retryRenewal = async (subId) => {
    try {
      const res = await API(`/api/user/reboost-subs/${subId}/retry`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) {
        if (data.error?.includes('Insufficient')) { setModal('retry-fail'); return; }
        throw new Error(data.error);
      }
      if (onBalanceChange) onBalanceChange();
      loadSubs();
      setModal(null);
      if (sel?.id === subId) setSel(prev => ({ ...prev, status: 'active', failReason: null }));
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    }
  };

  // Pause / Resume
  const togglePause = async (id) => {
    try {
      const res = await API(`/api/user/reboost-subs/${id}/toggle`, { method: 'PATCH' });
      if (res.ok) {
        const data = await res.json();
        loadSubs();
        if (sel?.id === id) setSel(data.subscription);
      }
    } catch {}
  };

  // Cancel
  const cancelSub = async (id) => {
    try {
      const res = await API(`/api/user/reboost-subs/${id}`, { method: 'DELETE' });
      if (res.ok) {
        loadSubs();
        setModal(null);
        setSel(null);
        setView('list');
      }
    } catch {}
  };

  // Loading state
  if (loading || configLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0' }}>
        <div className="rb-spinner" />
        <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: '1rem' }}>Loading subscriptions…</p>
      </div>
    );
  }

  // Out of stock state
  if (!isInStock && subs.filter(s => s.status !== 'cancelled').length === 0) {
    return (
      <div className="rb-wrap">
        <div className="rb-header">
          <div>
            <h1 className="rb-title">
              <span className="rb-icon-box">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
              </span>
              Auto-<span style={{ color: 'var(--accent)' }}>Reboost</span>
            </h1>
            <p className="rb-subtitle">Set it and forget it — boosts renew automatically.</p>
          </div>
        </div>
        <div className="rb-empty">
          <div style={{ fontSize: 42, marginBottom: 12 }}>📦</div>
          <h3>Currently Out of Stock</h3>
          <p>Auto-Reboost subscriptions are unavailable while boost tokens are out of stock. Check back soon!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rb-wrap">
      {/* Header */}
      <div className="rb-header">
        <div>
          <h1 className="rb-title">
            <span className="rb-icon-box">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
            </span>
            Auto-<span style={{ color: 'var(--accent)' }}>Reboost</span>
          </h1>
          <p className="rb-subtitle">Set it and forget it — boosts renew automatically.</p>
        </div>
        <div className="rb-header-actions">
          {view === 'list' ? (
            <button className="db-submit rb-new-btn" onClick={() => setView('create')} disabled={!isInStock}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              {isInStock ? 'New Subscription' : 'Out of Stock'}
            </button>
          ) : (
            <button className="rb-back-btn" onClick={() => { setView('list'); setSel(null); setMsg(null); }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></svg>
              Back
            </button>
          )}
        </div>
      </div>

      {/* Stats Row */}
      {view === 'list' && (
        <div className="rb-stats">
          <div className="rb-stat">
            <span className="rb-stat-emoji">⚡</span>
            <div><div className="rb-stat-val" style={{ color: 'var(--accent)' }}>{activeCount}</div><div className="rb-stat-label">Active</div></div>
          </div>
          <div className="rb-stat">
            <span className="rb-stat-emoji">🚀</span>
            <div><div className="rb-stat-val">{totalBoosts}</div><div className="rb-stat-label">Total boosts</div></div>
          </div>
          {failedCount > 0 && (
            <div className="rb-stat rb-stat-err">
              <span className="rb-stat-emoji">⚠️</span>
              <div><div className="rb-stat-val" style={{ color: 'var(--red-primary)' }}>{failedCount}</div><div className="rb-stat-label" style={{ color: 'var(--red-primary)' }}>Failed</div></div>
            </div>
          )}
        </div>
      )}

      {/* ═══ LIST VIEW ═══ */}
      {view === 'list' && (
        <>
          {failedCount > 0 && (
            <div className="rb-alert rb-alert-err">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--red-primary)" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span><strong>{failedCount} subscription{failedCount > 1 ? 's' : ''} failed to renew</strong> — insufficient balance. Add funds and retry.</span>
            </div>
          )}

          {subs.filter(x => x.status !== 'cancelled').length === 0 ? (
            <div className="rb-empty">
              <div style={{ fontSize: 42, marginBottom: 12 }}>🔄</div>
              <h3>No subscriptions yet</h3>
              <p>Create one to keep your server boosted automatically.</p>
              <button className="db-submit" style={{ marginTop: 16, maxWidth: 260 }} onClick={() => setView('create')}>
                Create Your First Subscription
              </button>
            </div>
          ) : (
            <div className="rb-list-card">
              {subs.filter(x => x.status !== 'cancelled').map((sub, i, arr) => {
                const sp = ALL_PLANS.find(p => p.id === sub.plan) || ALL_PLANS[0];
                const pr = calcPrice(sub.boosts, sub.pricePerBoost, sp, sub.billingMode, sub.tokenType);
                const isFailed = sub.status === 'failed';
                const isPaused = sub.status === 'paused';
                return (
                  <div key={sub.id}>
                    {i > 0 && <div className="rb-divider" />}
                    <div
                      className={`rb-row ${isFailed ? 'rb-row-failed' : ''}`}
                      onClick={() => { setSel(sub); setView('detail'); }}
                    >
                      <div className={`rb-row-icon ${isFailed ? 'rb-row-icon-err' : isPaused ? 'rb-row-icon-muted' : ''}`}>
                        {sub.server?.icon && sub.server.icon.startsWith('http')
                          ? <img src={sub.server.icon} alt="" style={{ width: 28, height: 28, borderRadius: 6 }} />
                          : <span style={{ fontSize: 20 }}>{sub.server?.icon || '🎯'}</span>
                        }
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="rb-row-top">
                          <span className="rb-row-name">{sub.server?.name || 'Unknown'}</span>
                          <span className={`db-badge ${isFailed ? 'db-bg-err' : sub.status === 'active' ? 'db-bg-ok' : 'rb-badge-muted'}`}>
                            {isFailed ? '⚠ Failed' : sub.status === 'active' ? '● Active' : '⏸ Paused'}
                          </span>
                          <span className="db-badge rb-badge-muted">
                            {sub.billingMode === 'upfront' ? '💎 Upfront' : '🔄 Recurring'}
                          </span>
                        </div>
                        <div className="rb-row-meta">
                          <span>{sub.boosts}x</span>
                          <span className="rb-dot">·</span>
                          <span style={{ textTransform: 'capitalize' }}>{sub.plan}</span>
                          <span className="rb-dot">·</span>
                          {sub.billingMode === 'upfront'
                            ? <span style={{ color: 'var(--accent)', fontWeight: 500 }}>${pr.total.toFixed(2)} upfront</span>
                            : <span style={{ color: 'var(--orb-orange)', fontWeight: 500 }}>${pr.monthly.toFixed(2)}/mo from balance</span>
                          }
                        </div>
                        {isFailed && sub.failReason && <div className="rb-row-fail">{sub.failReason}</div>}
                      </div>
                      <div className="rb-row-right">
                        {sub.status === 'active' && sub.nextRenewal && (
                          <div>
                            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Renews in</div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600 }}><Countdown date={sub.nextRenewal} /></div>
                          </div>
                        )}
                        {isFailed && (
                          <button className="rb-retry-btn" onClick={e => { e.stopPropagation(); retryRenewal(sub.id); }}>Retry</button>
                        )}
                      </div>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="rb-info-box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" style={{ marginTop: 1, flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            <div>
              <strong style={{ color: 'var(--text-primary)' }}>How it works:</strong>{' '}
              <span style={{ color: 'var(--accent)' }}>💎 Upfront</span> — pay the full period now, get up to 25% off.{' '}
              <span style={{ color: 'var(--orb-orange)' }}>🔄 Recurring</span> — we charge your balance monthly; if your balance is too low the renewal fails and boosts won't deliver until you retry. Cancel anytime, no refunds.
            </div>
          </div>
        </>
      )}

      {/* ═══ CREATE VIEW ═══ */}
      {view === 'create' && (
        <div className="rb-create">
          {done ? (
            <div className="rb-success-card">
              <div className="rb-success-icon">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <h3>Subscription Created!</h3>
              <p>Your boosts will renew automatically.</p>
            </div>
          ) : (
            <>
              <h2 className="rb-create-title">New <span style={{ color: 'var(--accent)' }}>Subscription</span></h2>

              {/* Stock/Token Type Info */}
              {tokenType === '3m' && (
                <div className="rb-hint-box rb-hint-orange rb-fadein" style={{ marginBottom: 16 }}>
                  🔒 <strong style={{ color: 'var(--orb-orange)' }}>3-Month tokens in stock</strong> — only <strong>Quarterly</strong> and <strong>Annual</strong> plans are available. Pay-as-you-go charges per cycle (every {planObj.days} days).
                </div>
              )}

              {/* Server Invite */}
              <div className="rb-section">
                <label className="rb-label">Server Invite</label>
                <input
                  type="text" className="db-input" placeholder="discord.gg/invite or invite code"
                  value={invite} onChange={e => setInvite(e.target.value)}
                />
                {inviteLoading && <span className="rb-field-hint">Resolving invite…</span>}
                {inviteOk && (
                  <div className="rb-invite-resolved">
                    {inviteOk.icon
                      ? <img src={inviteOk.icon} alt="" style={{ width: 28, height: 28, borderRadius: 6 }} />
                      : <span style={{ fontSize: 22 }}>🎯</span>
                    }
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{inviteOk.name}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{(inviteOk.members || 0).toLocaleString()} members</div>
                    </div>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" style={{ marginLeft: 'auto' }}><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                )}
              </div>

              {/* Billing Mode */}
              <div className="rb-section">
                <label className="rb-label">Billing Type</label>
                <div className="rb-billing-grid">
                  {BILLING_MODES.map(m => (
                    <div
                      key={m.id}
                      className={`rb-select-card ${billing === m.id ? 'on' : ''}`}
                      onClick={() => setBilling(m.id)}
                    >
                      <div style={{ fontSize: 18, marginBottom: 4 }}>{m.icon}</div>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 2 }}>{m.label}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                        {m.id === 'recurring' && tokenType === '3m'
                          ? 'Charged per cycle from your balance. No discount, but lower entry cost.'
                          : m.desc}
                      </div>
                      {billing === m.id && <div className="rb-select-bar" />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Plan selection — show for upfront always, and for recurring with 3m tokens */}
              {(billing === 'upfront' || (billing === 'recurring' && tokenType === '3m')) && (
                <div className="rb-section rb-fadein">
                  <label className="rb-label">Billing Cycle</label>
                  <div className="rb-plan-grid">
                    {PLANS.map(p => (
                      <div
                        key={p.id}
                        className={`rb-select-card ${plan === p.id ? 'on' : ''}`}
                        onClick={() => setPlan(p.id)}
                      >
                        {p.badge && billing === 'upfront' && (
                          <span className={`db-badge ${p.id === 'annual' ? 'db-bg-warn' : 'rb-badge-purple'}`} style={{ position: 'absolute', top: 8, right: 8 }}>
                            {p.badge}
                          </span>
                        )}
                        <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 2 }}>{p.label}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {billing === 'upfront' ? `${p.months}mo upfront` : `Charged every ${p.days} days`}
                        </div>
                        {plan === p.id && <div className="rb-select-bar" />}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {billing === 'recurring' && (
                <div className="rb-hint-box rb-hint-orange rb-fadein">
                  {tokenType === '3m' ? (
                    <>💡 <strong style={{ color: 'var(--orb-orange)' }}>Pay-as-you-go</strong> charges <strong style={{ color: 'var(--text-primary)' }}>${price.total.toFixed(2)} every {planObj.days} days</strong> ({planObj.label.toLowerCase()} cycle) from your balance. If your balance is too low when renewal is due, boosts won't be delivered until you add funds and retry.</>
                  ) : (
                    <>💡 <strong style={{ color: 'var(--orb-orange)' }}>Pay-as-you-go</strong> charges <strong style={{ color: 'var(--text-primary)' }}>${(qty * ppb).toFixed(2)}/month</strong> from your balance. If your balance is too low when renewal is due, boosts won't be delivered until you add funds and retry.</>
                  )}
                </div>
              )}

              {/* Boost count */}
              <div className="rb-section">
                <label className="rb-label">Boost Amount</label>
                <div className="db-amount-grid">
                  {BOOST_PRESETS.map(b => (
                    <button key={b} className={`db-amt-btn ${qty === b ? 'on' : ''}`} onClick={() => setQty(b)}>{b}x</button>
                  ))}
                </div>
                <div className="rb-perks-box">
                  <div style={{ fontSize: '0.68rem', color: 'var(--purple)', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Server Perks Unlocked</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {qty >= 2 && <span className="db-badge rb-badge-purple">Level 1</span>}
                    {qty >= 7 && <span className="db-badge rb-badge-purple">Level 2</span>}
                    {qty >= 14 && <span className="db-badge db-bg-warn">Level 3</span>}
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      {qty >= 14 ? '🎉 Max perks — 100 emoji, 384kbps, banner' : qty >= 7 ? '50 emoji, 256kbps, splash' : 'Custom bg, 128kbps audio'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="rb-section rb-summary-card">
                <label className="rb-label">Price Breakdown</label>

                {billing === 'upfront' ? (
                  <>
                    <div className="rb-price-row">
                      <span>{qty}x × ${ppb.toFixed(2)} × {planObj.months}mo</span>
                      <span>${price.fullPrice.toFixed(2)}</span>
                    </div>
                    {tokenType === '3m' && (
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: -4, marginBottom: 6 }}>
                        Using 3-month token pricing (${ppb.toFixed(2)}/boost)
                      </div>
                    )}
                    {price.discount > 0 && (
                      <div className="rb-price-row" style={{ color: 'var(--accent)' }}>
                        <span>{planObj.label} discount ({planObj.discount}%)</span>
                        <span style={{ fontWeight: 600 }}>-${price.discount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="rb-divider" style={{ margin: '10px 0' }} />
                    <div className="rb-price-total">
                      <div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>Pay now</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>Full {planObj.interval} · renews in {planObj.days} days</div>
                      </div>
                      <span className="rb-big-price">${price.total.toFixed(2)}</span>
                    </div>
                    {planObj.months > 1 && (
                      <div className="rb-savings-row">
                        <span>Effective: <strong style={{ color: 'var(--text-primary)' }}>${price.perMonth.toFixed(2)}/mo</strong> vs ${price.monthly.toFixed(2)}/mo</span>
                        <span className="db-badge db-bg-ok">Save ${price.discount.toFixed(2)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="rb-price-row">
                      <span>{qty}x × ${ppb.toFixed(2)}{tokenType === '3m' ? ` × ${planObj.months}mo` : ''}</span>
                      <span>${price.total.toFixed(2)}</span>
                    </div>
                    <div className="rb-divider" style={{ margin: '10px 0' }} />
                    <div className="rb-price-total">
                      <div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                          Due now &amp; every {tokenType === '3m' ? `${planObj.days} days` : 'month'}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>Auto-deducted from balance</div>
                      </div>
                      <span className="rb-big-price" style={{ color: 'var(--orb-orange)' }}>${price.total.toFixed(2)}</span>
                    </div>
                    <div className="rb-hint-box rb-hint-orange" style={{ marginTop: 10 }}>
                      ⚠️ If balance is below <strong style={{ color: 'var(--text-primary)' }}>${price.total.toFixed(2)}</strong> at renewal, boosts won't deliver. You can retry after adding funds.
                    </div>
                  </>
                )}

                {/* Balance check */}
                {(() => {
                  const due = price.total;
                  const ok = balance >= due;
                  return (
                    <div className={`rb-balance-check ${ok ? '' : 'err'}`}>
                      {ok ? (
                        <>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                          <span>Balance: <strong style={{ color: 'var(--accent)' }}>${balance.toFixed(2)}</strong> — covers ${due.toFixed(2)}</span>
                        </>
                      ) : (
                        <>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--red-primary)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                          <span style={{ color: 'var(--red-primary)' }}>Balance: <strong>${balance.toFixed(2)}</strong> — need <strong>${(due - balance).toFixed(2)}</strong> more</span>
                        </>
                      )}
                    </div>
                  );
                })()}

                <div className="rb-cancel-note">
                  <strong style={{ color: 'var(--text-primary)' }}>Cancellation:</strong> Cancel anytime. Boosts stay until end of paid period. No refunds.
                </div>

                {msg && <div className={`db-msg ${msg.type}`} style={{ marginTop: 10 }}>{msg.text}</div>}

                <button
                  className="db-submit"
                  style={{ width: '100%', marginTop: 14, padding: '0.75rem 1rem', fontSize: '0.88rem' }}
                  disabled={!inviteOk || busy || balance < price.total}
                  onClick={handleCreate}
                >
                  {busy ? (
                    <span className="rb-spinner-small" />
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                      Pay ${price.total.toFixed(2)} &amp; Start
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══ DETAIL VIEW ═══ */}
      {view === 'detail' && sel && (() => {
        const sp = ALL_PLANS.find(p => p.id === sel.plan) || ALL_PLANS[0];
        const pr = calcPrice(sel.boosts, sel.pricePerBoost, sp, sel.billingMode, sel.tokenType);
        const isFailed = sel.status === 'failed';
        return (
          <div className="rb-detail">
            <div className="rb-section">
              <div className="rb-detail-header">
                <div className={`rb-row-icon rb-detail-icon ${isFailed ? 'rb-row-icon-err' : ''}`}>
                  {sel.server?.icon && sel.server.icon.startsWith('http')
                    ? <img src={sel.server.icon} alt="" style={{ width: 32, height: 32, borderRadius: 8 }} />
                    : <span style={{ fontSize: 24 }}>{sel.server?.icon || '🎯'}</span>
                  }
                </div>
                <div style={{ flex: 1 }}>
                  <div className="rb-row-top">
                    <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>{sel.server?.name || 'Unknown'}</h2>
                    <span className={`db-badge ${isFailed ? 'db-bg-err' : sel.status === 'active' ? 'db-bg-ok' : 'rb-badge-muted'}`}>
                      {isFailed ? '⚠ Failed' : sel.status === 'active' ? '● Active' : '⏸ Paused'}
                    </span>
                    <span className="db-badge rb-badge-muted">
                      {sel.billingMode === 'upfront' ? '💎 Upfront' : '🔄 Recurring'}
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: 2 }}>
                    {(sel.server?.members || 0).toLocaleString()} members · {sel.serverInvite}
                  </div>
                </div>
              </div>

              {isFailed && sel.failReason && (
                <div className="rb-fail-banner">
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--red-primary)', marginBottom: 4 }}>Renewal Failed</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 10 }}>{sel.failReason}</div>
                  <button className="rb-retry-btn rb-retry-lg" onClick={() => retryRenewal(sel.id)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                    Retry Now (${pr.monthly.toFixed(2)} from balance)
                  </button>
                </div>
              )}

              <div className="rb-detail-grid">
                {[
                  { l: 'Plan', v: sp?.label },
                  { l: 'Billing', v: sel.billingMode === 'upfront' ? '💎 Upfront' : '🔄 Recurring' },
                  { l: 'Boosts', v: `${sel.boosts}x` },
                  { l: sel.billingMode === 'upfront' ? 'Paid upfront' : 'Monthly charge', v: sel.billingMode === 'upfront' ? `$${pr.total.toFixed(2)}` : `$${pr.monthly.toFixed(2)}/mo` },
                  { l: 'Next renewal', v: sel.nextRenewal ? new Date(sel.nextRenewal).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—' },
                  { l: 'Renewals', v: sel.renewalCount || 0 },
                ].map((x, i) => (
                  <div key={i} className="rb-detail-cell">
                    <div className="rb-detail-cell-label">{x.l}</div>
                    <div className="rb-detail-cell-val">{x.v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rb-section">
              <label className="rb-label">Actions</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {!isFailed && (
                  <button className="rb-action-btn" onClick={() => togglePause(sel.id)}>
                    {sel.status === 'active' ? (
                      <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>Pause</>
                    ) : (
                      <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>Resume</>
                    )}
                  </button>
                )}
                <button className="rb-action-btn rb-action-danger" onClick={() => setModal('cancel')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                  Cancel Subscription
                </button>
              </div>
            </div>

            <div className="rb-cancel-note" style={{ marginBottom: '2rem' }}>
              <strong style={{ color: 'var(--text-primary)' }}>Cancellation:</strong> Boosts stay until end of paid period
              {sel.nextRenewal ? ` (${new Date(sel.nextRenewal).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})` : ''}.
              No refunds.
            </div>

            {/* Cancel modal */}
            {modal === 'cancel' && (
              <div className="rb-modal-overlay" onClick={() => setModal(null)}>
                <div className="rb-modal" onClick={e => e.stopPropagation()}>
                  <div className="rb-modal-icon rb-modal-icon-danger">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--red-primary)" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  </div>
                  <h3>Cancel subscription?</h3>
                  <p>Boosts for <strong style={{ color: 'var(--text-primary)' }}>{sel.server?.name}</strong> stay active until the end of your paid period.</p>
                  <p style={{ fontSize: '0.78rem', marginTop: 4 }}>No refund. No further charges.</p>
                  <div className="rb-modal-actions">
                    <button className="rb-action-btn" onClick={() => setModal(null)}>Keep</button>
                    <button className="rb-action-btn rb-action-danger" onClick={() => cancelSub(sel.id)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Retry fail modal */}
      {modal === 'retry-fail' && (
        <div className="rb-modal-overlay" onClick={() => setModal(null)}>
          <div className="rb-modal" onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>💸</div>
            <h3>Still not enough balance</h3>
            <p>Add funds to your account first, then retry the renewal.</p>
            <button className="db-submit" style={{ width: '100%', marginTop: 16 }} onClick={() => setModal(null)}>Got it</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoReboost;
